import { AxiosError } from 'axios';
import { getErrorInfo } from '../constants/error-keys';
import {
  createErrorTranslationKey,
  ErrorSubType,
  ErrorTranslationKeys,
} from '../constants/error-translation-keys';
import { HttpStatus } from '../constants/http-status';

/**
 * Creates a translation key for backend error messages
 *
 * @param error - The Axios error object containing response data
 * @returns A translation key string or undefined if no error message is found
 *
 * @example
 * // For unique constraint error
 * // Returns "error:back-end:unique-constraint:pet-name-exists"
 * createBackendErrorTranslationKey({
 *   response: {
 *     status: 422,
 *     data: {
 *       errors: {
 *         unique_constraint: ['A pet with this name already exists for this customer']
 *       }
 *     }
 *   }
 * });
 *
 * // For generic error
 * // Returns "error:back-end:some_error"
 * createBackendErrorTranslationKey({
 *   response: {
 *     status: 400,
 *     data: {
 *       error: 'some_error'
 *     }
 *   }
 * });
 */
export const createBackendErrorTranslationKey = (error: AxiosError): string | undefined => {
  const { status } = error.response || {};

  if (status === HttpStatus.UNPROCESSABLE_CONTENT && error.response?.data?.errors) {
    const errorEntries = Object.entries(error.response.data.errors);
    if (errorEntries.length > 0) {
      const [errorKey, errorMessages] = errorEntries[0];
      const messages = Array.isArray(errorMessages) ? errorMessages : [errorMessages];
      const errorInfo = getErrorInfo(errorKey, messages);

      if (errorInfo) {
        if (errorInfo.matchedMessage && errorInfo.translationKey) {
          return createErrorTranslationKey(
            errorInfo.messageKey,
            errorInfo.translationKey as ErrorSubType
          );
        } else {
          return createErrorTranslationKey(
            errorInfo.messageKey,
            ErrorTranslationKeys.UNIQUE_CONSTRAINT.TYPES.CUSTOM
          );
        }
      }
    }
  }

  return error.response?.data?.error ? `error:back-end:${error.response.data.error}` : undefined;
};
